import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import api from '@/lib/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (error) {
      // Even on error, show success to not reveal if email exists
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl mb-2">Passwort vergessen</h1>
          <p className="text-[#666666]">
            {submitted 
              ? 'Prüfen Sie Ihren Posteingang'
              : 'Geben Sie Ihre E-Mail-Adresse ein, um Ihr Passwort zurückzusetzen'
            }
          </p>
        </div>

        {/* Success State */}
        {submitted ? (
          <div className="bg-white border border-[#E5E5E5] p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="font-heading text-xl mb-2">E-Mail gesendet</h2>
              <p className="text-[#666666] mb-6">
                Falls ein Konto mit der E-Mail-Adresse <strong>{email}</strong> existiert, 
                haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet.
              </p>
              <p className="text-sm text-[#999999] mb-6">
                Der Link ist 1 Stunde gültig. Prüfen Sie auch Ihren Spam-Ordner.
              </p>
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-2 text-[#0A0A0A] hover:text-gold transition-colors"
              >
                <ArrowLeft size={18} />
                Zurück zum Login
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-4 mb-6 flex items-center gap-3">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white border border-[#E5E5E5] p-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    E-Mail-Adresse
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 pl-12 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none"
                      placeholder="admin@example.com"
                      data-testid="forgot-email"
                    />
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999]" />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0A0A0A] text-white py-3 font-medium hover:bg-[#1A1A1A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <div className="spinner w-5 h-5 border-white" />
                  ) : (
                    <>
                      <Mail size={18} />
                      Reset-Link senden
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Back to Login */}
            <div className="text-center mt-6">
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-2 text-[#666666] hover:text-[#0A0A0A] transition-colors"
              >
                <ArrowLeft size={18} />
                Zurück zum Login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
